import { TranslocoModule, TranslocoService, provideTranslocoScope, translate } from '@jsverse/transloco';
import { Location } from '@angular/common';
import { Component, DestroyRef, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { HeaderService } from 'core';
import { CustomerConfigurationService, CustomerConfigurationState } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-configure-customer-banking',
  imports: [ReactiveFormsModule, MatButtonModule, MatCardModule, MatCheckboxModule, MatFormFieldModule, MatInputModule, MatSelectModule, RouterLink, TranslocoModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './configure-customer-banking.html',
  styleUrl: './configure-customer-banking.scss',
})
export class ConfigureCustomerBanking implements OnInit, OnDestroy {
  readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);

  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  readonly customer = computed(() => this.configuration.proposalId() === this.proposalId ? this.configuration.customer() : null);
  readonly form = new FormGroup({
    paymentType: new FormControl<string | null>(null),
    abiDescription: new FormControl('', { nonNullable: true }),
    cabDescription: new FormControl('', { nonNullable: true }),
    sdiCode: new FormControl('', { nonNullable: true }),
    vatExempt: new FormControl(false, { nonNullable: true }),
  });
  // TODO: Caricare le opzioni dalla futura API. Le descrizioni non passano da Transloco.
  readonly paymentTypes = [
    { value: 'bank-transfer', get label() { return translate<string>('orders.ui.bonificoBancario'); } },
    { value: 'bank-receipt', get label() { return translate<string>('orders.ui.ricevutaBancaria'); } },
    { value: 'direct-remittance', get label() { return translate<string>('orders.ui.rimessaDiretta'); } },
  ];
  readonly maxFileBytes = 10 * 1024 * 1024;
  readonly dragging = signal(false);
  readonly fileError = signal<'bankingFileTypeError' | 'bankingFileSizeError' | 'bankingSingleFileError' | null>(null);

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));
    this.configuration.restoreFlowState(this.location.getState() as Partial<CustomerConfigurationState> | null, this.proposalId);
    if (this.customer()) {
      // Solo ripristino della bozza digitata dall'utente: nessun prefill dai dati del cliente.
      this.form.setValue(this.configuration.banking());
    }
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (this.customer()) {
        this.configuration.banking.set(this.form.getRawValue());
      }
    });
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  chooseFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.attachFile(file);
    }
    input.value = '';
  }

  dragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(true);
  }

  dropFile(event: DragEvent): void {
    event.preventDefault();
    this.dragging.set(false);
    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }
    if (files.length !== 1) {
      this.fileError.set('bankingSingleFileError');
      return;
    }
    this.attachFile(files[0]);
  }

  removeFile(): void {
    this.configuration.bankingDocument.set(null);
    this.fileError.set(null);
  }

  private attachFile(file: File): void {
    if (!/\.(pdf|jpe?g|png)$/i.test(file.name) || (file.type && !['application/pdf', 'image/jpeg', 'image/png'].includes(file.type))) {
      this.fileError.set('bankingFileTypeError');
      return;
    }
    if (file.size > this.maxFileBytes) {
      this.fileError.set('bankingFileSizeError');
      return;
    }
    this.fileError.set(null);
    // Nessun upload automatico: il documento verrà inviato solo dalla futura API del flusso.
    this.configuration.bankingDocument.set(file);
  }

  continue(): void {
    if (!this.customer() || !this.configuration.destinationForConfiguration() || this.fileError()) {
      return;
    }
    const value = this.form.getRawValue();
    this.configuration.banking.set({
      ...value,
      abiDescription: value.abiDescription.trim(),
      cabDescription: value.cabDescription.trim(),
      sdiCode: value.sdiCode.trim(),
    });
    // TODO: Inviare dati bancari e documento alla futura API.
    void this.router.navigate(['/new-order-proposal/configure-customer/order-notes'], {
      queryParams: { id: this.proposalId },
      state: this.configuration.getFlowState(),
    });
  }
}
